import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { FocusShell } from "@/components/layout/FocusShell";
import { Button, ButtonLink, Notice, Panel } from "@/components/system";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import { ApiError } from "@/lib/api";
import { SEO } from "@/lib/seo";
import {
  peekWaitReport,
  reportedWaitBucketLabel,
  verifyWaitReport,
} from "@/lib/wait-reports";
import "@/styles/phase2-demand.css";

export default function ReportVerify() {
  const { token = "" } = useParams<{ token: string }>();
  const { city } = useCity();
  const [state, setState] = useState<"idle" | "submitting" | "success" | "duplicate" | "error">(
    "idle",
  );
  const peek = useQuery({
    queryKey: ["wait-report-verify", city.slug, token],
    queryFn: () => peekWaitReport(city.slug, token),
    enabled: Boolean(token),
    retry: false,
  });

  const verify = async () => {
    setState("submitting");
    try {
      await verifyWaitReport(city.slug, token);
      track("wait_report_verify");
      setState("success");
    } catch (error) {
      setState(error instanceof ApiError && error.status === 409 ? "duplicate" : "error");
    }
  };

  return (
    <FocusShell>
      <SEO
        title="Verify your GP wait-time report · PulseBoard"
        description="Verify an anonymous GP wait-time report for the Canberra referral directory."
        path={`/report/verify/${token}`}
        noindex
      />
      <Panel>
        <p className="ds-eyebrow ds-eyebrow--teal">GP wait-time report</p>
        {peek.isLoading ? <p className="ds-small" role="status">Checking your verification link…</p> : null}
        {peek.isError ? (
          <div className="ds-stack" style={{ gap: 8 }} role="alert">
            <h1 className="ds-display-2">This verification link has expired</h1>
            <p className="ds-small">It may have been used already. You can submit a new report from the specialist's profile.</p>
          </div>
        ) : null}
        {peek.data && state !== "success" && state !== "duplicate" ? (
          <div className="ds-stack" style={{ gap: 14 }}>
            <h1 className="ds-display-2">Verify your report</h1>
            <p className="ds-body">
              <strong>{peek.data.facilityName}</strong> · seen {reportedWaitBucketLabel(peek.data.seenBucket ?? peek.data.bucket)}
            </p>
            <div>
              <Button disabled={state === "submitting"} onClick={() => void verify()}>
                {state === "submitting" ? "Verifying…" : "Verify report"}
              </Button>
            </div>
            {state === "error" ? <Notice tone="error">Verification didn't go through. Open the link from your email again.</Notice> : null}
          </div>
        ) : null}
        {state === "success" ? (
          <div className="ds-stack" style={{ gap: 8 }} role="status">
            <h1 className="ds-display-2">Thanks, your report is verified</h1>
            <p className="ds-body">It counts towards the public figures once there are at least three reports.</p>
          </div>
        ) : null}
        {state === "duplicate" ? (
          <div className="ds-stack" style={{ gap: 8 }} role="status">
            <h1 className="ds-display-2">Thanks, you've already reported this one</h1>
            <p className="ds-body">One verified report per email and specialist counts every 90 days.</p>
          </div>
        ) : null}
        <div><ButtonLink to="/directory" variant="secondary">Back to the directory</ButtonLink></div>
      </Panel>
    </FocusShell>
  );
}
